import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { fetchUserInfo, type UserInfo } from "../api/user";
import { AuthContext } from "../context/auth.context";
import type { JwtPayload } from "../types/auth";
import { getCurrentActiveTariff } from "../utils/tariffTimeline";

export const useProvideCurrentUserInfo = () => {
  const { user } = useContext(AuthContext);
  const uid = (user as JwtPayload | null | undefined)?.uid;
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(uid != null);
  const [resolved, setResolved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState(0);

  const refetch = useCallback(() => setToken((t) => t + 1), []);

  useEffect(() => {
    if (uid == null) {
      setUserInfo(null);
      setLoading(false);
      setResolved(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchUserInfo(uid)
      .then((data) => {
        if (!cancelled) setUserInfo(data);
      })
      .catch(() => {
        if (!cancelled) {
          setUserInfo(null);
          setError("Не удалось загрузить информацию о тарифе");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setResolved(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [uid, token]);

  const activeTariffs = useMemo(() => userInfo?.activeTariffs ?? [], [userInfo?.activeTariffs]);
  const activeTariff = useMemo(() => getCurrentActiveTariff(activeTariffs), [activeTariffs]);
  const requestedTariffs = userInfo?.requestedTariffs ?? [];
  const policySetting = userInfo?.policySetting;
  const userLimits = userInfo?.userLimits ?? null;

  return {
    uid,
    userInfo,
    activeTariffs,
    activeTariff,
    requestedTariffs,
    policySetting,
    userLimits,
    loading,
    resolved,
    error,
    refetch,
  };
};

export type CurrentUserInfoValue = ReturnType<typeof useProvideCurrentUserInfo>;
